<template>
  <IonSearchbar
    ref="searchbar"
    v-model="searchValue"
    :animated="animated"
    :minlength="minlength"
    :maxlength="maxlength"
    :placeholder="placeholder"
    :clear-icon="closeOutline"
    show-clear-button="always"
    enterkeyhint="search"
    @ionInput="onInput"
    @ionChange="onSearch"
    @ionCancel="onCancel"
    :class="['custom-ion-searchbar', `size-${size}`]"
    :style="searchbarStyle"
  />
</template>

<script lang="ts">
import { ComponentColorType, ComponentSizeType } from "@/types/componentType";
import { IonSearchbar } from "@ionic/vue";
import { closeOutline, searchCircleOutline } from "ionicons/icons";
import { PropType, computed, defineComponent, ref } from "vue";

export default defineComponent({
  name: "CustomIonSearchbar",
  components: { IonSearchbar },
  props: {
    placeholder: { type: String, default: "Search" },
    initialValue: { type: String, required: false },
    animated: { type: Boolean, default: false },
    minlength: { type: Number, default: undefined },
    maxlength: { type: Number, default: undefined },
    size: { type: String as PropType<ComponentSizeType>, default: "default" },
    color: { type: String as PropType<ComponentColorType>, default: "primary" },
  },
  setup(props, { emit }) {
    const searchValue = ref(props.initialValue || "");
    const searchbar = ref<InstanceType<typeof IonSearchbar> | null>(null);

    async function setFocus(): Promise<void> {
      await searchbar.value?.$el?.setFocus?.();
    }

    const searchbarStyle = computed(() => {
      const iconColor = props.color ? `var(--color-${props.color})` : "var(--color-text-lighter)";
      return {
        "--icon-color": iconColor,
        "--clear-button-color": iconColor,
      };
    });

    function onInput(event: CustomEvent) {
      searchValue.value = event.detail.value;
      emit("onSearch", searchValue.value.trim());
    }

    function onSearch(event: CustomEvent) {
      searchValue.value = event.detail.value;
      emit("onSearch", searchValue.value.trim());
    }

    function onCancel() {
      searchValue.value = "";
      emit("onSearch", searchValue.value);
    }

    return {
      searchbar,
      searchValue,
      searchbarStyle,
      searchCircleOutline,
      closeOutline,
      setFocus,
      onInput,
      onSearch,
      onCancel,
    };
  },
});
</script>

<style lang="scss">
ion-searchbar.custom-ion-searchbar {
  width: 100% !important;
  padding: 0 !important;
  min-height: var(--control-height) !important;
  --color: var(--color-text);
  --background: var(--color-light);
  --placeholder-color: var(--color-text-lighter);
  --box-shadow: none;
  /* Ionic's iOS theme floors this container at 36px. Without this override the
     container stays taller than the input on the small/default tiers, and the
     absolutely-positioned search + clear icons (height: 100%) overflow it. */
  .searchbar-input-container {
    min-height: var(--control-height) !important;
  }
  .searchbar-input {
    border: var(--control-border-width) solid var(--control-border-color) !important;
    border-radius: var(--control-radius) !important;
  }
  &:focus-within .searchbar-input {
    border-color: var(--control-border-color-focus) !important;
  }
  /* Ionic's iOS theme sizes the input from its own 6px vertical padding + line-height,
     which lands ~2px above the control ladder. Pin the height and adopt --control-pad-y
     (as CustomIonInput does) so the searchbar matches every other input tier-for-tier. */
  input {
    font-size: var(--control-font) !important;
    min-height: var(--control-height) !important;
    height: var(--control-height) !important;
    padding-top: var(--control-pad-y) !important;
    padding-bottom: var(--control-pad-y) !important;
  }
  .searchbar-search-icon {
    width: var(--control-icon) !important;
  }
  button.searchbar-clear-button {
    width: var(--searchbar-clear-button-w, 2.5rem) !important;
  }
  .searchbar-clear-icon {
    width: var(--searchbar-clear-icon-w, 1.25rem) !important;
  }
  &.size-small {
    --searchbar-clear-button-w: 2.25rem;
    --searchbar-clear-icon-w: 1.1rem;
  }
  &.size-large {
    --searchbar-clear-button-w: 2.75rem;
    --searchbar-clear-icon-w: 1.4rem;
  }
}
</style>
